import { useEffect, useMemo, useState } from 'react';
import {
  useWorkflowExecutionsInfinite,
  type WorkflowExecution,
} from '@/hooks/useWorkflowExecutions';
import { DATE_GROUP_ORDER, getDateGroup, type DateGroup } from '@/utils/date';
import { extractUserMessageText } from '@/utils/chatHistoryTransform';
import { FEATURE_AREAS, useTrackListSearch } from '@/lib/analytics';

/** Standard debounce for a search-a-list input in this codebase. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The timestamp used for grouping: the first user message's `updated_at` when it
 * exists, otherwise the execution's own. That answers "when was this
 * conversation actually used" rather than when the row was touched.
 */
const timestampOf = (execution: WorkflowExecution): string => {
  const firstUserMessage = execution.workflow_execution_chat_history?.find(
    message => message.role === 'user',
  );
  return firstUserMessage?.updated_at ?? execution.updated_at;
};

/**
 * Display text for a conversation. Prefers the persisted title (a user rename,
 * or the server's auto-title on the first user message) and only derives from
 * chat history when it is still null.
 */
export const titleOf = (execution: WorkflowExecution): string => {
  if (execution.title) return execution.title;

  const history = execution.workflow_execution_chat_history;
  if (!history || history.length === 0) return 'New conversation';

  const firstUserMessage = history.find(message => message.role === 'user');
  if (!firstUserMessage?.content) return 'Empty conversation';

  return extractUserMessageText(firstUserMessage.content);
};

export interface ChatDateGroup {
  group: DateGroup;
  executions: WorkflowExecution[];
}

/**
 * Search, paging and date bucketing for the chat list.
 *
 * Buckets reuse the shared `DATE_GROUP_ORDER` / `getDateGroup` helpers rather
 * than defining their own, so the sidebar cannot drift from anywhere else that
 * groups by relative date. The API returns rows newest-first, so preserving
 * insertion order within a bucket keeps them sorted.
 */
export function useChatList(workflowInstanceId: string) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [search]);

  const {
    executions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useWorkflowExecutionsInfinite(
    workflowInstanceId,
    Boolean(workflowInstanceId),
    debouncedSearch,
  );

  // Search here is server-side, so the count is only meaningful once the
  // debounced term has been applied and the request has settled; until then it
  // is withheld and the event fires with usage but no result count.
  const searchApplied = debouncedSearch === search.trim();
  useTrackListSearch({
    surface: 'chat_history',
    featureArea: FEATURE_AREAS.chat,
    query: search,
    resultCount: searchApplied && !isLoading ? executions?.length : undefined,
  });

  const groups = useMemo<ChatDateGroup[]>(() => {
    if (!executions || executions.length === 0) return [];

    const buckets = new Map<DateGroup, WorkflowExecution[]>();
    for (const execution of executions) {
      const group = getDateGroup(timestampOf(execution));
      const bucket = buckets.get(group) ?? [];
      bucket.push(execution);
      buckets.set(group, bucket);
    }

    return DATE_GROUP_ORDER.flatMap(group => {
      const bucket = buckets.get(group);
      return bucket ? [{ group, executions: bucket }] : [];
    });
  }, [executions]);

  return {
    search,
    setSearch,
    isSearching: debouncedSearch.length > 0,
    groups,
    // A request that failed says nothing about whether the user has chats, so
    // `error` disqualifies "empty" rather than being left to callers to
    // remember. Getting this wrong tells someone their history is gone.
    isEmpty: !isLoading && !error && groups.length === 0,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
}
